// DagbestedingPage.jsx - Dagbesteding activiteiten planning
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Clock,
  MapPin,
  Check,
  Dumbbell,
} from 'lucide-react';
import { PageHeader } from './shared';

const DagbestedingPage = ({ onBack, user, selectedLocation }) => {
  const [selectedDay, setSelectedDay] = useState(new Date().getDay() || 7); // 1=Ma, 7=Zo
  const [selectedActiviteit, setSelectedActiviteit] = useState(null);
  
  const dagen = [
    { nr: 1, naam: 'Maandag', kort: 'Ma' },
    { nr: 2, naam: 'Dinsdag', kort: 'Di' },
    { nr: 3, naam: 'Woensdag', kort: 'Wo' },
    { nr: 4, naam: 'Donderdag', kort: 'Do' },
    { nr: 5, naam: 'Vrijdag', kort: 'Vr' },
    { nr: 6, naam: 'Zaterdag', kort: 'Za' },
    { nr: 7, naam: 'Zondag', kort: 'Zo' },
  ];

  // Activiteiten voor woensdag t/m zondag
  const vrije_activiteiten = [
    { naam: 'Groepswandeling', icon: '🚶', duur: '45-60 min', beschrijving: 'Samen wandelen in de buurt' },
    { naam: 'Krachttraining', icon: '💪', duur: '30-45 min', beschrijving: 'Oefeningen met gewichten of lichaamseigen weerstand' },
    { naam: 'Basketballen', icon: '🏀', duur: '45-60 min', beschrijving: 'Basketballen op het veldje' },
    { naam: 'Voetballen', icon: '⚽', duur: '45-60 min', beschrijving: 'Potje voetbal' },
    { naam: 'Tuinieren', icon: '🌱', duur: '30-60 min', beschrijving: 'Werken in de tuin' },
    { naam: 'Koekjes bakken', icon: '🍪', duur: '60-90 min', beschrijving: 'Samen bakken als dessert activiteit' },
    { naam: 'Fietsen', icon: '🚴', duur: '45-90 min', beschrijving: 'Fietstocht maken' },
    { naam: 'Twister', icon: '🌀', duur: '30-45 min', beschrijving: 'Twister spelen - dansspelletje met kleuren' },
    { naam: 'Bingo avond', icon: '🎲', duur: '60-90 min', beschrijving: 'Gezellige bingo avond' },
  ];

  const getDagInfo = (dagNr) => {
    switch(dagNr) {
      case 1: // Maandag
        return {
          type: 'extern',
          titel: 'Externe Dagbesteding',
          locatie: 'Team Smits, Tilburg',
          tijden: { start: '12:30', eind: '15:30' },
          activiteit: 'Fitness of Kickboksen',
          icon: '🥊',
          beschrijving: 'Elke maandag gaan de bewoners naar Team Smits in Tilburg voor fitness of kickboksen.',
          instructies: [
            'Bewoners verzamelen om 12:30',
            'Vertrek naar Team Smits',
            'Activiteit van 13:00 tot 15:00',
            'Terug om 15:30'
          ]
        };
      case 2: // Dinsdag
        return {
          type: 'rust',
          titel: 'Rustdag',
          icon: '😴',
          beschrijving: 'Dinsdag is een rustdag. Geen geplande groepsactiviteiten.',
          instructies: [
            'Individuele activiteiten naar keuze',
            'Rustig dagje',
            'Tijd voor persoonlijke zaken'
          ]
        };
      default: // Woensdag t/m Zondag
        return {
          type: 'keuze',
          titel: 'Vrije Activiteiten',
          icon: '🎯',
          beschrijving: 'Begeleider kiest een of meerdere activiteiten voor de dag. Bewoners mogen meedoen naar keuze.',
          instructies: [
            'Kies een passende activiteit',
            'Bepaal het tijdstip',
            'Nodig bewoners uit om mee te doen',
            'Zorg voor benodigde materialen'
          ]
        };
    }
  };

  const dagInfo = getDagInfo(selectedDay);
  const vandaagNr = new Date().getDay() || 7;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30" data-testid="dagbesteding-page">
      <PageHeader 
        title="Dagbesteding" 
        subtitle="Activiteiten planning" 
        onBack={onBack} 
        icon={Dumbbell} 
        iconGradient="from-emerald-500 to-teal-500" 
      />

      <div className="px-4 py-6 pb-32">
        {/* Dag selector */}
        <div className="mb-6">
          <div className="flex gap-1 bg-white/80 p-1.5 rounded-2xl shadow-sm overflow-x-auto">
            {dagen.map(dag => (
              <button
                key={dag.nr}
                onClick={() => setSelectedDay(dag.nr)}
                className={`flex-1 min-w-[44px] px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  selectedDay === dag.nr
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg'
                    : dag.nr === vandaagNr
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {dag.kort}
              </button>
            ))}
          </div>
        </div>

        {/* Dag informatie */}
        <motion.div
          key={selectedDay}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className={`p-5 rounded-2xl ${
            dagInfo.type === 'extern' 
              ? 'bg-gradient-to-br from-orange-400 to-orange-500 text-white'
              : dagInfo.type === 'rust'
              ? 'bg-gradient-to-br from-blue-400 to-blue-500 text-white'
              : 'bg-gradient-to-br from-emerald-500 to-teal-500 text-white'
          }`}>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center text-3xl">
                {dagInfo.icon}
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold">{dagen.find(d => d.nr === selectedDay)?.naam}</h2>
                <p className="text-white/90 font-medium">{dagInfo.titel}</p>
                {dagInfo.tijden && (
                  <p className="text-white/80 text-sm mt-1">
                    <Clock className="w-4 h-4 inline mr-1" />
                    {dagInfo.tijden.start} - {dagInfo.tijden.eind}
                  </p>
                )}
                {dagInfo.locatie && (
                  <p className="text-white/80 text-sm">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    {dagInfo.locatie}
                  </p>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Beschrijving */}
        <div className="mb-6 p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm">
          <p className="text-slate-700">{dagInfo.beschrijving}</p>
          
          {dagInfo.instructies && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <h4 className="font-semibold text-slate-800 mb-2">Instructies:</h4>
              <ul className="space-y-1.5">
                {dagInfo.instructies.map((instr, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {instr}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Activiteiten keuze (alleen voor woensdag-zondag) */}
        {dagInfo.type === 'keuze' && (
          <div>
            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">
              Kies een activiteit
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {vrije_activiteiten.map((act, idx) => (
                <motion.button
                  key={act.naam}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  onClick={() => setSelectedActiviteit(act)}
                  className={`p-4 rounded-2xl text-left transition-all ${
                    selectedActiviteit?.naam === act.naam
                      ? 'bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-lg'
                      : 'bg-white border border-slate-200/50 shadow-sm hover:shadow-md hover:border-emerald-200'
                  }`}
                >
                  <span className="text-2xl block mb-2">{act.icon}</span>
                  <h4 className={`font-semibold text-sm ${selectedActiviteit?.naam === act.naam ? 'text-white' : 'text-slate-800'}`}>
                    {act.naam}
                  </h4>
                  <p className={`text-xs mt-1 ${selectedActiviteit?.naam === act.naam ? 'text-white/80' : 'text-slate-500'}`}>
                    {act.duur}
                  </p>
                </motion.button>
              ))}
            </div>

            {selectedActiviteit && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-4 p-4 rounded-2xl bg-emerald-50 border border-emerald-200"
              >
                <h4 className="font-semibold text-emerald-800 flex items-center gap-2">
                  <span className="text-xl">{selectedActiviteit.icon}</span>
                  {selectedActiviteit.naam}
                </h4>
                <p className="text-sm text-emerald-700 mt-2">{selectedActiviteit.beschrijving}</p>
                <p className="text-xs text-emerald-600 mt-2">
                  <Clock className="w-3 h-3 inline mr-1" />
                  Duur: {selectedActiviteit.duur}
                </p>
              </motion.div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DagbestedingPage;
